import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { facilityMapRequestAtom } from "atoms/map/request/facilities";
import axios from "axios";
import { DEFAULT_MAX_BINS } from "constants/mapSeries.constants";
import { useAtomValue } from "jotai";
import { setGlobalGroupBy } from "store/features";
import { RootState } from "store/rootReducer";
import {
  ConfirmationChoice,
  selectFacilityHasToConfirmSize,
  useHideConfirmationDebounced,
  useMapStore
} from "stores/map";
import styled from "styled-components";
import { toPdenEnum } from "utils/toPdenEnum";

import { IGroupBy } from "models";
import { EntityKind } from "models/entityKind";

import { getStoredBinsFromSession } from "components/filter/util/getStoredBinsFromSession";
import { setStoredBinsToSession } from "components/filter/util/setStoredBinsToSession";
import ConfirmDialog from "components/shared/ConfirmDialog";

import { useFacilityMapUpdater } from "./hooks/useFacilityMapUpdater";

const dataRoot = process.env.REACT_APP_DATA_RS_ROOT;

function FacilityMapSeriesSizeConfirmation(): JSX.Element {
  const dispatch = useDispatch();

  const facilityMapRequest = useAtomValue(facilityMapRequestAtom);

  const { fetchFacilityMap, cancelFacilityRequest } = useFacilityMapUpdater();

  const setFacilityHasToConfirmSize = useMapStore((s) => s.setFacilityHasToConfirmSize);
  const setFacilityConfirmationChoice = useMapStore(
    (s) => s.setFacilityConfirmationChoice
  );
  const hasToConfirmSize = useMapStore(selectFacilityHasToConfirmSize);
  const { hideFacilityConfirmation } = useHideConfirmationDebounced();

  const useNormalizeBy = useSelector(
    (state: RootState) => state.normalizeBy.useNormalizeBy
  );
  const groupBy = useSelector((state: RootState) => state.groupBy.globalFacilityFocus);
  const globalNormalizeBy = useSelector(
    (state: RootState) => state.normalizeBy.globalNormalizeBy
  );
  const facilityFilterId = useSelector(
    (state: RootState) => state.facilityFilter.filterId
  );
  const [isDisabled, setDisabled] = useState(false);

  const canEditBin = !(
    groupBy?.dataType.toLowerCase() === "text" ||
    groupBy?.dataType.toLowerCase() === "date"
  );

  useEffect(() => {
    setFacilityHasToConfirmSize(false);
  }, [setFacilityHasToConfirmSize, groupBy.groupByField]);

  const getDefaultBinSize = async (fieldGb: IGroupBy, filterId: string) => {
    const API_URL = `${dataRoot}/api/v1/data/bin-size/${filterId}/${btoa(
      encodeURIComponent(fieldGb.property)
    )}?normalizeField=${useNormalizeBy ? globalNormalizeBy.field : "''"}&per=${
      useNormalizeBy ? globalNormalizeBy.per : 0
    }&pdenSource=${toPdenEnum(fieldGb.pdenSource)}&entityKind=${
      EntityKind.Facility
    }&useDynamicBins=${fieldGb.bin?.UseDynamicBins}`;

    try {
      const response = await axios.get(API_URL);
      if (response.status === 200) {
        return response.data;
      } else {
        throw new Error(`Failed to fetch bin size: ${response.statusText}`);
      }
    } catch (error) {
      return null;
    }
  };

  const onConfirm = async () => {
    setDisabled(true);

    setFacilityConfirmationChoice(ConfirmationChoice.Confirm);

    // Cancel any pending request before making the new one with override
    cancelFacilityRequest();

    if (facilityMapRequest) {
      const requestWithOverride = {
        ...facilityMapRequest,
        OverrideSizeLimit: true
      };
      await fetchFacilityMap(requestWithOverride);
    }
    hideFacilityConfirmation();
    setDisabled(false);
  };

  const onCancel = async () => {
    setDisabled(true);

    setFacilityConfirmationChoice(ConfirmationChoice.Cancel);

    const updateStoredBins = (property: string, binSettings: typeof groupBy.bin) => {
      const storedBins = getStoredBinsFromSession();
      storedBins[property] = { ...binSettings };
      setStoredBinsToSession(storedBins);
    };

    try {
      let newGroupBy;

      if (canEditBin) {
        const defaultBins = await getDefaultBinSize(groupBy, facilityFilterId.id);

        const newBin = {
          ...groupBy.bin,
          BinSize: defaultBins.binSize ?? "",
          LessThan: defaultBins.lessThan ?? "",
          GreaterThan: defaultBins.greaterThan ?? "",
          MaxBins: DEFAULT_MAX_BINS
        };

        newGroupBy = Object.assign({}, groupBy, {
          bin: newBin,
          entityKind: EntityKind.Facility
        });

        updateStoredBins(groupBy.property, newBin);
      } else {
        const newBin = {
          ...groupBy.bin,
          MaxBins: DEFAULT_MAX_BINS
        };
        newGroupBy = Object.assign({}, groupBy, {
          bin: newBin,
          entityKind: EntityKind.Facility
        });

        updateStoredBins(groupBy.property, newBin);
      }

      setFacilityHasToConfirmSize(false);
      dispatch(setGlobalGroupBy(newGroupBy));
    } catch (error) {
      const newBin = {
        ...groupBy.bin,
        MaxBins: DEFAULT_MAX_BINS,
        BinSize: DEFAULT_MAX_BINS
      };

      const newGroupBy = Object.assign({}, groupBy, {
        bin: newBin,
        entityKind: EntityKind.Facility
      });
      updateStoredBins(groupBy.property, newBin);
      setFacilityHasToConfirmSize(false);
      dispatch(setGlobalGroupBy(newGroupBy));
    } finally {
      setDisabled(false);
    }
  };

  return (
    <ConfirmIndicator className={`${!hasToConfirmSize ? "hidden" : ""}`}>
      <ConfirmDialog
        title="Large number of series"
        message="You are about to generate a large number of facility series which may slow down the browser."
        onConfirm={() => onConfirm()}
        onClose={() => onCancel()}
        disabled={isDisabled}
      />
    </ConfirmIndicator>
  );
}

export default FacilityMapSeriesSizeConfirmation;

const ConfirmIndicator = styled.div`
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(211, 211, 211, 0.424);
  z-index: 9999;

  &.hidden {
    display: none;
  }

  .MuiPaper-root {
    max-width: 320px;
  }

  .MuiCardContent-root {
    padding: 10px 36px 26px 36px;
  }

  .title {
    font-size: 2.2rem;
    font-weight: var(--fontWeightBold);
    color: var(--color-text);
  }

  .content {
    p {
      font-size: 1.4rem;
      font-weight: var(--fontWeightMedium);
      margin-bottom: 15px;
    }

    .button {
      height: 38px;
      width: 100px;
      border-radius: 4px;

      span {
        font-size: 1.6rem;
      }
    }

    .actions {
      gap: 10px;
    }
  }
`;
